import { useState } from "react";
import axios from "axios";
import { Lock, Mail, User, AlertCircle, CheckCircle2, ArrowLeft } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  // modes: login | register | forgot | forgot-sent
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else if (mode === "register") {
        if (!form.name.trim()) throw new Error("Ad soyad gerekli");
        if (form.password.length < 6) throw new Error("Şifre en az 6 karakter olmalı");
        await register(form.name, form.email, form.password);
      } else if (mode === "forgot") {
        await axios.post(`${API}/auth/forgot-password`, { email: form.email });
        setMode("forgot-sent");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message || "İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const goto = (next) => {
    setError("");
    setMode(next);
  };

  const title = {
    login: "Hesabınıza giriş yapın",
    register: "Yeni hesap oluşturun",
    forgot: "Şifrenizi mi unuttunuz?",
    "forgot-sent": "E-postanızı kontrol edin",
  }[mode];

  const description = {
    login: "Portföyünüze her cihazdan güvenli erişim.",
    register: "Portföyünüzü buluttan takip etmek için bir hesap açın.",
    forgot: "E-postanızı yazın, size sıfırlama bağlantısı gönderelim.",
    "forgot-sent": "1 saat içinde geçerli sıfırlama bağlantısı gönderdik.",
  }[mode];

  return (
    <div className="auth-shell">
      <div className="auth-card" data-testid="auth-card">
        <div className="auth-brand">
          <div className="brand-mark brand-logo" data-testid="brand-logo"><img src="/brand/logo-mark.png" alt="Sigorta CRM" /></div>
          <div>
            <strong>Sigorta<span>Crm</span></strong>
            <small>BİREYSEL SİGORTA TAKİP PANELİ</small>
          </div>
        </div>
        <div className="auth-slogan" data-testid="auth-slogan"><span>DAHA GÜÇLÜ İLİŞKİLER</span></div>
        <div className="auth-title">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>

        {mode === "forgot-sent" ? (
          <div className="auth-sent" data-testid="forgot-sent">
            <CheckCircle2 size={44} />
            <p><strong>{form.email}</strong> adresine bir sıfırlama bağlantısı gönderdik.</p>
            <p className="auth-sent-hint">E-postayı bulamıyorsanız spam klasörünü kontrol edin. Bağlantı 1 saat geçerlidir.</p>
            <button className="primary-button" data-testid="back-to-login" onClick={() => goto("login")}><ArrowLeft size={15} /> Giriş sayfasına dön</button>
          </div>
        ) : (
          <>
            {(mode === "login" || mode === "register") && (
              <>
                <button type="button" className="google-signin" data-testid="google-signin-button" onClick={loginWithGoogle}>
                  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.8 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3L37.5 9.5C34.1 6.3 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.2 18.9 12 24 12c3 0 5.8 1.1 7.9 3L37.5 9.5C34.1 6.3 29.3 4 24 4c-7.3 0-13.6 4-16.9 9.9z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.4-4.6 2.4-7.2 2.4-5.3 0-9.7-3.1-11.4-7.6l-6.5 5C9.9 40 16.4 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2c-.4.4 6.6-4.8 6.6-14.7 0-1.2-.1-2.4-.4-3.5z"/></svg>
                  Google ile {mode === "login" ? "giriş yap" : "kayıt ol"}
                </button>
                <div className="auth-divider"><span>veya</span></div>
              </>
            )}
            <form onSubmit={submit} className="auth-form">
              {mode === "register" && (
                <label className="auth-field">
                  <span>Ad Soyad</span>
                  <div className="auth-input"><User size={16} /><input data-testid="auth-name-input" name="name" placeholder="Can Çalışkan" value={form.name} onChange={change} autoComplete="name" required /></div>
                </label>
              )}
              <label className="auth-field">
                <span>E-posta</span>
                <div className="auth-input"><Mail size={16} /><input data-testid="auth-email-input" name="email" type="email" placeholder="can@example.com" value={form.email} onChange={change} autoComplete="email" required /></div>
              </label>
              {mode !== "forgot" && (
                <label className="auth-field">
                  <span>Şifre</span>
                  <div className="auth-input"><Lock size={16} /><input data-testid="auth-password-input" name="password" type="password" placeholder={mode === "register" ? "En az 6 karakter" : "Şifreniz"} value={form.password} onChange={change} autoComplete={mode === "login" ? "current-password" : "new-password"} required /></div>
                </label>
              )}
              {mode === "login" && (
                <div className="auth-row-between">
                  <button type="button" className="auth-inline-link" data-testid="forgot-password-link" onClick={() => goto("forgot")}>Şifremi unuttum</button>
                </div>
              )}
              {error && <div className="auth-error" data-testid="auth-error"><AlertCircle size={15} />{error}</div>}
              <button className="primary-button auth-submit" data-testid="auth-submit" disabled={busy}>
                {busy ? "İşleniyor…" : mode === "login" ? "Giriş yap" : mode === "register" ? "Hesap oluştur" : "Sıfırlama bağlantısı gönder"}
              </button>
            </form>
            <div className="auth-switch">
              {mode === "login" && <>Hesabın yok mu? <button data-testid="auth-switch-register" onClick={() => goto("register")}>Hemen kayıt ol</button></>}
              {mode === "register" && <>Zaten hesabın var mı? <button data-testid="auth-switch-login" onClick={() => goto("login")}>Giriş yap</button></>}
              {mode === "forgot" && <button data-testid="auth-back-login" onClick={() => goto("login")}><ArrowLeft size={13} /> Girişe dön</button>}
            </div>
          </>
        )}

        <div className="auth-privacy">Verileriniz size özeldir · bireysel sigorta portföyü için tasarlanmıştır</div>
      </div>
      <div className="auth-side" aria-hidden="true">
        <div className="auth-side-quote">
          <em data-testid="auth-side-quote-text">"Müşteri-Poliçe-Üretim-Yenileme-Randevu-Komisyon takip sistemi."</em>
          <span data-testid="auth-side-quote-author">— Can Çalışkan</span>
        </div>
      </div>
    </div>
  );
}
